// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { compose } from 'recompose';
import { isEmpty } from 'lodash/fp';
import { createStructuredSelector } from 'reselect';

// Utils
import editorStoreConnect from '../../../../../../components/editor/store/components/editorStoreConnect';
import { hasChanged } from '../../../../../../utils/react/propsComparisons';
import getFirstEntityInSelection from '../../../../../../components/editor/store/reducers/getFirstEntityInSelection';
import { selectAllText } from '../../../../../../utils/domUtil';
import { hasCommandModifier } from '../../../../../../utils/keyboard/keyboardUtility';

// Selectors
import { getEditorState } from '../../../../../../components/editor/store/reducers/editorStoreSelector';
import { getCurrentlyEditingId } from '../../../../../../element/selection/currentlyEditingSelector';

// Actions
import { editorEntityChange, editorEntityRemove } from '../../../../../../components/editor/store/editorActions';
import { setElementLocalData } from '../../../../../../element/local/elementLocalDataActions';
import { closePopup } from '../../../../../../components/popupPanel/popupActions';

// Components
import SearchClearButton from '../../../../../../components/form/SearchClearButton';

// Constants
import { ENTITIES } from '../../../../../../components/editor/richText/richTextConstants';
import { KEY_CODES } from '../../../../../../utils/keyboard/keyConstants';
import { DRAFT_ENTITY_MUTABILITY } from '../../../../../../components/editor/draftjsConstants';

// Styles
import './HyperlinkPopup.scss';
import { PopupIds } from '../../../../../../components/popupPanel/popupConstants';

const urlHasChanged = hasChanged('url');

const mapEditorStateToProps = (state) => {
    const editorState = getEditorState(state);
    const firstEntityKey = getFirstEntityInSelection(editorState);

    if (!firstEntityKey) return { url: '' };

    const contentState = editorState.getCurrentContent();
    const firstEntity = contentState.getEntity(firstEntityKey);

    if (!firstEntity || firstEntity.get('type') !== ENTITIES.LINK) return { url: '' };

    return { url: firstEntity.get('data').url || '' };
};

const mapEditorDispatchToProps = (dispatch) => ({
    dispatchEditorEntityChange: (url) => {
        dispatch(
            editorEntityChange({
                entityType: ENTITIES.LINK,
                mutability: DRAFT_ENTITY_MUTABILITY.MUTABLE,
                data: { url },
            }),
        );
    },
    dispatchEditorEntityRemove: () => dispatch(editorEntityRemove()),
});

const mapStateToProps = createStructuredSelector({
    currentlyEditingId: getCurrentlyEditingId,
});

const mapDispatchToProps = (dispatch) => ({
    dispatchClosePopup: () => dispatch(closePopup(PopupIds.HYPERLINK, false)),
    dispatchClearLocalEditorState: ({ id }) => dispatch(setElementLocalData({ id, data: { editorState: null } })),
});

class HyperlinkPopup extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            url: this.props.url || '',
        };
    }

    componentDidMount() {
        requestAnimationFrame(() => {
            this.inputComponent.focus();
            selectAllText(this.inputComponent);
        });

        document.addEventListener('pointerdown', this.handlePointerDown, { capture: true });
        document.addEventListener('keydown', this.handleKeyDown, { capture: true });
    }

    componentWillReceiveProps(nextProps) {
        if (urlHasChanged(this.props, nextProps)) {
            this.setState({ url: nextProps.url });
        }
    }

    componentWillUnmount() {
        const { dispatchClearLocalEditorState, currentlyEditingId } = this.props;
        dispatchClearLocalEditorState({ id: currentlyEditingId });

        document.removeEventListener('pointerdown', this.handlePointerDown, { capture: true });
        document.removeEventListener('keydown', this.handleKeyDown, { capture: true });
    }

    // Handle stripping of spaces or new lines here
    onChange = (event) => {
        const text = event.target.value;

        const url = text.replace(/[\s,\n]/g, '');

        this.setState({ url });
    };

    onKeyPress = (event) => {
        switch (event.which) {
            case KEY_CODES.ENTER:
                return this.onSubmit(event);
            default:
        }
    };

    onSubmit = (event) => {
        event.preventDefault();

        const { dispatchEditorEntityChange, dispatchEditorEntityRemove, dispatchClosePopup } = this.props;
        const { url } = this.state;

        dispatchClosePopup();

        isEmpty(url) ? dispatchEditorEntityRemove() : dispatchEditorEntityChange(url);
    };

    handlePointerDown = (event) => {
        if (this.component.contains(event.target)) return;

        event.preventDefault();
        event.stopPropagation();
        this.saveOrCancelChanges();
    };

    handleKeyDown = (event) => {
        if (hasCommandModifier(event) && event.which === KEY_CODES.K) {
            return this.cancelChanges(event);
        }

        switch (event.which) {
            case KEY_CODES.ESC:
                return this.cancelChanges(event);
            case KEY_CODES.TAB: {
                event.preventDefault();
                event.stopPropagation();
                return this.saveOrCancelChanges(event);
            }
            case KEY_CODES.SPACEBAR: {
                event.preventDefault();
                event.stopPropagation();
                return null;
            }
            default:
                return null;
        }
    };

    saveOrCancelChanges = () => {
        // Closing without a URL should remove the 'empty link entity' that was created when opening the popup
        if (isEmpty(this.state.url)) return this.removeHyperlink();

        this.props.dispatchEditorEntityChange(this.state.url);
        this.props.dispatchClosePopup();
    };

    cancelChanges = (event) => {
        const { dispatchEditorEntityChange, dispatchClosePopup } = this.props;

        event.preventDefault();

        // Cancelling without a saved URL should remove the 'empty link entity' that was created when
        // opening the popup
        if (isEmpty(this.props.url)) return this.removeHyperlink();

        // Otherwise remove the highlight data
        dispatchEditorEntityChange(this.props.url);
        dispatchClosePopup();
    };

    removeHyperlink = () => {
        const { dispatchEditorEntityRemove, dispatchClosePopup } = this.props;
        dispatchEditorEntityRemove();
        dispatchClosePopup();
    };

    render() {
        const { url } = this.state;

        return (
            <div
                className="HyperlinkPopup"
                ref={(c) => {
                    this.component = c;
                }}
            >
                <input
                    className="hyperlink-input"
                    tabIndex={-1}
                    ref={(c) => {
                        this.inputComponent = c;
                    }}
                    type="text"
                    placeholder="Enter a URL..."
                    onChange={this.onChange}
                    onKeyPress={this.onKeyPress}
                    value={url}
                />
                <SearchClearButton show={!!url} canClear={!!url} onPointerDown={this.removeHyperlink} />
            </div>
        );
    }
}

HyperlinkPopup.propTypes = {
    dispatchEditorEntityChange: PropTypes.func,
    dispatchEditorEntityRemove: PropTypes.func,
    dispatchClosePopup: PropTypes.func,
    dispatchClearLocalEditorState: PropTypes.func,
    currentlyEditingId: PropTypes.string,
    url: PropTypes.string,
};

const enhancer = compose(
    editorStoreConnect(mapEditorStateToProps, mapEditorDispatchToProps),
    connect(mapStateToProps, mapDispatchToProps),
);

export default enhancer(HyperlinkPopup);
